import { useAuth } from "@/features/auth/hooks/useAuth";
import { useCallback } from "react";
import type { Citation } from "../utils/citations";
import { isPaperSource, isPaperXmlType, isPmcId } from "../utils/citations";

const PAPERS_MCP_URL = "/mcp/papers";

const xmlHtmlCache = new Map<string, string>();
const metadataCache = new Map<string, Partial<Citation>>();
const metadataLoading = new Set<string>();

const getAuthHeaders = async (
  getToken: () => Promise<string | null>,
): Promise<Record<string, string>> => {
  const token = await getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const fetchXmlCitationHtml = async (
  citation: Citation,
  getToken: () => Promise<string | null>,
): Promise<string | null> => {
  const isPaperLink = citation.source_type === "paper_link";
  if (!isPaperXmlType(citation.source_type) && !isPaperLink) {
    return null;
  }

  const lineNumber = citation.line_number ?? 1;
  if (!citation.doc_id) return null;

  const cacheKey = `${citation.doc_id}-${lineNumber}-${citation.content?.slice(0, 50) || ""}`;
  const cached = xmlHtmlCache.get(cacheKey);
  if (cached) return cached;

  try {
    const url = new URL(
      `${PAPERS_MCP_URL}/citations/render/${citation.doc_id}/${lineNumber}`,
      window.location.origin,
    );
    if (citation.content) {
      url.searchParams.set("content", citation.content);
    }

    const response = await fetch(url.toString(), {
      headers: await getAuthHeaders(getToken),
    });

    if (!response.ok) return null;

    const html = await response.text();
    xmlHtmlCache.set(cacheKey, html);
    return html;
  } catch {
    return null;
  }
}

const fetchCitationMetadata = async (
  citation: Citation,
  getToken: () => Promise<string | null>,
): Promise<Partial<Citation> | null> => {
  const isBioMedRxivCitation =
    isPaperXmlType(citation.source_type) ||
    citation.source_type?.includes("supplement_pdf") ||
    citation.source_type === "paper_link" ||
    isPaperSource(citation.source) ||
    isPmcId(citation.doc_id);

  if (!isBioMedRxivCitation) return null;
  if (citation.doc_title || citation.authors) return null;
  if (!citation.doc_id) return null;

  const isPaperLevel = citation.line_number === undefined;
  const cacheKey = isPaperLevel
    ? `doc_${citation.doc_id}`
    : `${citation.doc_id}-${citation.line_number}`;

  const cached = metadataCache.get(cacheKey);
  if (cached) return cached;
  if (metadataLoading.has(cacheKey)) return null;

  metadataLoading.add(cacheKey);

  try {
    const url = isPaperLevel
      ? `${PAPERS_MCP_URL}/citations/document/${citation.doc_id}`
      : `${PAPERS_MCP_URL}/citations/metadata/${citation.doc_id}/${citation.line_number}`;

    const response = await fetch(url, {
      headers: await getAuthHeaders(getToken),
    });

    if (!response.ok) return null;

    const data = await response.json();
    const metadata: Partial<Citation> = {
      doc_title: data.doc_title,
      authors: data.authors,
      doi: data.doi,
      source: data.source,
      section: data.section,
      month_year: data.month_year,
      journal: data.journal,
      xml_id: data.xml_id,
      xpath: data.xpath,
      source_path: data.source_path,
      source_type: data.source_type,
      highlight_available: data.highlight_available,
      public_url: data.public_url,
    };

    metadataCache.set(cacheKey, metadata);
    return metadata;
  } catch {
    return null;
  } finally {
    metadataLoading.delete(cacheKey);
  }
}

export const usePaperCitationsApi = () => {
  const { getIdToken } = useAuth();

  const fetchHtml = useCallback(
    (citation: Citation) => fetchXmlCitationHtml(citation, getIdToken),
    [getIdToken],
  );
  const fetchMetadata = useCallback(
    (citation: Citation) => fetchCitationMetadata(citation, getIdToken),
    [getIdToken],
  );

  return { fetchXmlCitationHtml: fetchHtml, fetchCitationMetadata: fetchMetadata };
};
